import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Component that prevents automatic scroll-to-top on route changes
 * This works globally for all navigation, not just sidebar clicks
 * This is a GLOBAL solution that works at the Router level
 */
const ScrollPrevention: React.FC = () => {
  const location = useLocation();
  const savedScrollPosition = useRef<number>(0);
  const isNavigatingRef = useRef<boolean>(false);
  const scrollLockTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const previousPathnameRef = useRef<string>('');

  // Save scroll position BEFORE route change (useLayoutEffect runs synchronously)
  useLayoutEffect(() => {
    // Only save if pathname is about to change
    if (previousPathnameRef.current !== location.pathname) {
      // Save current scroll position IMMEDIATELY before route change
      savedScrollPosition.current = window.scrollY;
    }
  });

  useEffect(() => {
    // Only prevent scroll if pathname actually changed
    if (previousPathnameRef.current === location.pathname) {
      return;
    }
    
    // Use the saved scroll position from useLayoutEffect
    const scrollToPreserve = savedScrollPosition.current;
    previousPathnameRef.current = location.pathname;
    
    // IMMEDIATELY restore scroll position before React Router can scroll
    if (scrollToPreserve > 0) {
      window.scrollTo(0, scrollToPreserve);
    }
    
    // ALWAYS activate scroll prevention if we have a saved position
    // Even if position is 0, we want to prevent unwanted scrolls
    if (scrollToPreserve >= 0) {
      isNavigatingRef.current = true;
    } else {
      return;
    }

    // Prevent scroll-to-top by intercepting it immediately
    const preventScroll = () => {
      if (isNavigatingRef.current) {
        window.scrollTo(0, scrollToPreserve);
      }
    };

    // Override window.scrollTo temporarily - ALWAYS prevent scroll-to-top during navigation
    const originalScrollTo = window.scrollTo.bind(window);
    const scrollToOverride = function(...args: any[]) {
      if (isNavigatingRef.current) {
        let y = 0;
        if (args.length === 1 && typeof args[0] === 'object' && args[0] !== null) {
          y = (args[0] as any).top || 0;
        } else if (args.length >= 2) {
          y = args[1] || 0;
        }
        
        // ALWAYS prevent scroll-to-top during navigation
        // If trying to scroll to top (y === 0 or very small), restore saved position
        if (y === 0 || (y < 50 && scrollToPreserve > 50)) {
          originalScrollTo(0, scrollToPreserve);
          return;
        }
      }
      originalScrollTo.apply(window, args as any);
    };

    (window as any).scrollTo = scrollToOverride;

    // Continuously restore scroll position - VERY AGGRESSIVE
    const restoreInterval = setInterval(() => {
      if (isNavigatingRef.current) {
        const currentScroll = window.scrollY;
        // Zero tolerance - restore immediately if different
        if (currentScroll !== scrollToPreserve) {
          window.scrollTo(0, scrollToPreserve);
        }
      }
    }, 1); // Check every millisecond for maximum protection

    // Listen to scroll events and prevent them - ALWAYS prevent if navigating
    const handleScroll = (e: Event) => {
      if (isNavigatingRef.current) {
        const currentScroll = window.scrollY;
        // Prevent any scroll that moves away from saved position
        if (Math.abs(currentScroll - scrollToPreserve) > 1) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          window.scrollTo(0, scrollToPreserve);
          return false;
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: false, capture: true });
    document.addEventListener('scroll', handleScroll, { passive: false, capture: true });

    // Clear navigation lock after route change completes
    if (scrollLockTimeoutRef.current) {
      clearTimeout(scrollLockTimeoutRef.current);
    }
    
    scrollLockTimeoutRef.current = setTimeout(() => {
      isNavigatingRef.current = false;
      (window as any).scrollTo = originalScrollTo;
    }, 500);

    return () => {
      clearInterval(restoreInterval);
      window.removeEventListener('scroll', handleScroll, { capture: true });
      document.removeEventListener('scroll', handleScroll, { capture: true });
      if (scrollLockTimeoutRef.current) {
        clearTimeout(scrollLockTimeoutRef.current);
      }
    };
  }, [location.pathname]);

  return null; // This component doesn't render anything
};

export default ScrollPrevention;

